import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import FunctionalDialog from 'components/Modals/FunctionalDialog';
import general from 'services/locales/en/general';
import { userService } from 'services/api';
import { Nullable } from 'types';

type EmailConfirmationDialogMethods = {
  onEmailConfirmationDialogShouldOpen: () => void;
  displayEmailConfirmationDialog: () => Nullable<
    ReactElement<typeof FunctionalDialog>
  >;
};

const useEmailConfirmationDialog = (): EmailConfirmationDialogMethods => {
  const { t } = useTranslation();

  const [isEmailConfirmationDialogOpen, setIsEmailConfirmationDialogOpen] =
    useState(false);

  const onResendEmailConfirmationClick = () => {
    userService.resendConfirmationEmail();
    setIsEmailConfirmationDialogOpen(false);
  };

  const onEmailConfirmationDialogShouldOpen = () => {
    setIsEmailConfirmationDialogOpen(true);
  };

  const displayEmailConfirmationDialog = (): Nullable<
    ReactElement<typeof FunctionalDialog>
  > => {
    if (!isEmailConfirmationDialogOpen) return null;
    return (
      <FunctionalDialog
        title={t(general.emailConfirmation.verificationModal.title)}
        description={t(general.emailConfirmation.verificationModal.description)}
        closeText={t(general.emailConfirmation.action.resend)}
        submitText={t(general.action.ok)}
        state={{
          isOpen: isEmailConfirmationDialogOpen,
          isLoading: false,
          error: undefined,
        }}
        action={{
          onSubmit: () => setIsEmailConfirmationDialogOpen(false),
          onClose: () => onResendEmailConfirmationClick(),
          onExited: () => setIsEmailConfirmationDialogOpen(false),
        }}
        buttonVariant="contained"
      />
    );
  };

  return {
    onEmailConfirmationDialogShouldOpen,
    displayEmailConfirmationDialog,
  };
};

export default useEmailConfirmationDialog;
